import React, { useState } from 'react';
import DocumentMeta from 'react-document-meta';
import { Link } from 'react-router-dom';
import menuData from '../testData.json';
import MenuShowcase from '../components/MenuShowcase';
import { useI18n } from '../i18n/I18nContext';
import styles from '../styles/Menu.module.css';

const publicUrl = process.env.PUBLIC_URL || '';

const imageUrl = (picture, group) => {
  const folder = group === 'Tea' && !picture.startsWith('tea/') ? 'tea/' : '';
  const path = `${folder}${picture}`;
  const extension = /\.(png|jpe?g|webp)$/i.test(path) ? '' : '.webp';
  return `${publicUrl}/images/${path}${extension}`;
};

const takeItems = (group, count = 3) => (
  (menuData.data[group] || []).slice(0, count).map(item => ({
    ...item,
    image: imageUrl(item.picture, group),
    descriptionKey: `pages.menu.descriptions.${group.toLowerCase()}`,
  }))
);

const menuGroups = [
  { id: 'bar', titleKey: 'pages.menu.barTitle', link: '/Bar', items: takeItems('Bar') },
  { id: 'coffee', titleKey: 'pages.menu.coffeeTitle', link: '/Coffee', items: takeItems('Coffee') },
  { id: 'tea', titleKey: 'pages.menu.teaTitle', link: '/Tea', items: takeItems('Tea') },
  { id: 'desserts', titleKey: 'pages.menu.dessertsTitle', link: '/Desserts', items: takeItems('Desserts') },
];

const showcase = {
  featured: [
    {
      id: 'breakfast',
      titleKey: 'pages.menu.featured.breakfastTitle',
      subtitleKey: 'pages.menu.featured.breakfastText',
      image: `${publicUrl}/images/gallery/gallery5.webp`,
      alt: 'Fresh breakfast table',
      to: '/Coffee',
    },
    {
      id: 'lunch',
      titleKey: 'pages.menu.featured.lunchTitle',
      subtitleKey: 'pages.menu.featured.lunchText',
      image: `${publicUrl}/images/gallery/gallery7.webp`,
      alt: 'Grain Bites lunch dish',
      to: '/Bar',
    },
  ],
  columns: [
    {
      id: 'breakfast-items',
      titleKey: 'pages.menu.columns.breakfast',
      items: [
        {
          id: 'artisan-toast',
          nameKey: 'pages.menu.items.morningEggToast',
          ingredientsKey: 'pages.menu.ingredients.morningEggToast',
          price: '3,50 €',
          image: `${publicUrl}/images/breads/dinkel-toast.webp`,
          alt: 'Morning egg toast',
        },
        {
          id: 'fresh-bread',
          nameKey: 'pages.menu.items.breakfastPlate',
          ingredientsKey: 'pages.menu.ingredients.breakfastPlate',
          price: '4,50 €',
          image: `${publicUrl}/images/gallery/gallery5.webp`,
          alt: 'Breakfast plate',
        },
        {
          id: 'sweet-kozunak',
          nameKey: 'pages.menu.items.sweetKozunak',
          ingredientsKey: 'pages.menu.ingredients.sweetKozunak',
          price: '5,00 €',
          image: `${publicUrl}/images/breads/kozunak4.webp`,
          alt: 'Homemade kozunak',
        },
        {
          id: 'seasonal-plate',
          nameKey: 'pages.menu.items.seasonalPlate',
          ingredientsKey: 'pages.menu.ingredients.seasonalPlate',
          price: '8,50 €',
          image: `${publicUrl}/images/gallery/gallery2.webp`,
          alt: 'Seasonal food plate',
        },
      ],
    },
    {
      id: 'lunch-items',
      titleKey: 'pages.menu.columns.lunch',
      items: [
        {
          id: 'grain-bowl',
          nameKey: 'pages.menu.items.grainBowl',
          ingredientsKey: 'pages.menu.ingredients.grainBowl',
          price: '9,50 €',
          image: `${publicUrl}/images/gallery/gallery3.webp`,
          alt: 'Grain Bites lunch bowl',
        },
        {
          id: 'shared-table',
          nameKey: 'pages.menu.items.sharedTable',
          ingredientsKey: 'pages.menu.ingredients.sharedTable',
          price: '12,00 €',
          image: `${publicUrl}/images/gallery/gallery4.webp`,
          alt: 'Shared lunch table',
        },
        {
          id: 'wholegrain-loaf',
          nameKey: 'pages.menu.items.warmLunchPlate',
          ingredientsKey: 'pages.menu.ingredients.warmLunchPlate',
          price: '5,50 €',
          image: `${publicUrl}/images/gallery/gallery7.webp`,
          alt: 'Warm lunch plate',
        },
        {
          id: 'sweet-finish',
          nameKey: 'pages.menu.items.sweetFinish',
          ingredientsKey: 'pages.menu.ingredients.sweetFinish',
          price: '6,00 €',
          image: `${publicUrl}/images/gallery/gallery.webp`,
          alt: 'A sweet dessert plate',
        },
      ],
    },
  ],
};

const Menu = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const { t } = useI18n();

  const meta = {
    title: 'Menu | Grain Bites',
    description: 'Explore the Grain Bites menu of food, drinks, artisan bread, and desserts.',
  };

  return (
    <DocumentMeta {...meta}>
      <main className={styles.page}>
        <header
          className={styles.hero}
          style={{ backgroundImage: `url(${publicUrl}/images/hero/header4.webp)` }}
        >
          <p className={styles.heroScript}>{t('pages.menu.heroLabel')}</p>
          <h1>{t('pages.menu.heroTitle')}</h1>
          <span className={styles.heroRule} aria-hidden="true" />
          <p className={styles.heroText}>
            {t('pages.menu.heroText')}
          </p>
          <p className={styles.heroPassion}>{t('pages.menu.heroPassion')}</p>
          <p className={styles.heroFlavours}>{t('pages.menu.heroFlavours')}</p>
        </header>

        <section className={styles.menuArea} aria-labelledby="menu-title">
          <div className={styles.menuIntro}>
            <p className={styles.kicker}>{t('pages.menu.introLabel')}</p>
            <h2 id="menu-title">{t('pages.menu.introTitle')}</h2>
            <p>{t('pages.menu.introText')}</p>
          </div>

          <div className={styles.menuGrid}>
            {menuGroups.map(group => (
              <article className={styles.group} key={group.id}>
                <div className={styles.groupHead}>
                  <h3>{t(group.titleKey)}</h3>
                  <Link to={`${publicUrl}${group.link}`} aria-label={`${t('pages.menu.openMenu')} ${t(group.titleKey)}`}>
                    {t('pages.menu.viewAll')}
                  </Link>
                </div>

                <div className={styles.rule} aria-hidden="true" />

                <div className={styles.items}>
                  {group.items.map(item => (
                    <article className={styles.item} key={item.id}>
                      <button
                        className={styles.itemImage}
                        type="button"
                        onClick={() => setSelectedItem(item)}
                        aria-label={`${t('pages.menu.openDetails')} ${item.name}`}
                      >
                        <img src={item.image} alt="" loading="lazy" decoding="async" />
                      </button>
                      <div className={styles.itemInfo}>
                        <div className={styles.itemTop}>
                          <h4>{item.name}</h4>
                          <span className={styles.dots} aria-hidden="true" />
                          <strong>{item.price}</strong>
                        </div>
                        <p>{t(item.descriptionKey)}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <MenuShowcase
            featured={showcase.featured}
            columns={showcase.columns}
          />
        </section>

        {selectedItem && (
          <div
            className={styles.modalLayer}
            role="presentation"
            onMouseDown={event => {
              if (event.target === event.currentTarget) setSelectedItem(null);
            }}
          >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="menu-item-title">
              <button
                className={styles.modalClose}
                type="button"
                onClick={() => setSelectedItem(null)}
                aria-label="Close details"
              >
                ×
              </button>
              <img
                className={styles.modalImage}
                src={selectedItem.image}
                alt={selectedItem.name}
                decoding="async"
              />
              <div className={styles.modalCopy}>
                <p className={styles.kicker}>{selectedItem.category}</p>
                <h2 id="menu-item-title">{selectedItem.name}</h2>
                <p>{t(selectedItem.descriptionKey)}</p>
                {selectedItem.contain && selectedItem.contain.length > 0 && (
                  <p className={styles.ingredients}>
                    <strong>{t('pages.menu.ingredientsLabel')}:</strong> {selectedItem.contain.join(' / ')}
                  </p>
                )}
                <strong className={styles.modalPrice}>{selectedItem.price}</strong>
              </div>
            </div>
          </div>
        )}
      </main>
    </DocumentMeta>
  );
};

export default Menu;
